import { useFeatureFlagEnabled, useFeatureFlagPayload, useFeatureFlagVariantKey } from '@posthog/react/slim';
import { isUndefined } from 'lodash-es';

import { checkVariant } from '../utils/helpers/check-variant';
import { CONTROL, PayloadsType, TEST, VariantType } from '../utils/types';
import { useFeatureFlagCookieData } from './use-feature-flag-cookie-data';
import { useGetFeatureFlagModule } from './use-get-feature-flag-module';

export const transformMultiVariantData = <T>(variant: VariantType, payloads: unknown) => ({
  isTest: checkVariant(variant, TEST),
  variant,
  payload: ((payloads as PayloadsType<T>)?.[variant] ?? (payloads as PayloadsType<T>)?.[CONTROL] ?? payloads) as T,
});

const checkIsNewVariant = <T>(
  isFeatureEnabled: boolean,
  postHogVariant: string,
  postHogPayload: T,
  payloads: PayloadsType<T>,
  bootstrapVariant: VariantType,
  cookieVariant: VariantType,
  cookiePayload: T,
) => {
  if (!isFeatureEnabled || isUndefined(payloads)) return false;

  if (!isFeatureEnabled) {
    return (
      !((cookieVariant as string) in payloads) &&
      typeof cookiePayload === typeof payloads[bootstrapVariant] &&
      !Object.values(payloads).includes(cookiePayload)
    );
  }

  return (
    isFeatureEnabled &&
    !((postHogVariant as string) in payloads) &&
    typeof postHogPayload === typeof payloads[bootstrapVariant] &&
    !Object.values(payloads).includes(postHogPayload)
  );
};

export const useMultipleVariantData = <T = boolean>(featureFlagName: string) => {
  const { payloads, bootstrapVariant, isSettled } = useGetFeatureFlagModule<T>(featureFlagName);

  const isFeatureEnabled = useFeatureFlagEnabled(featureFlagName);
  const postHogVariant = useFeatureFlagVariantKey(featureFlagName);
  const postHogPayload = useFeatureFlagPayload(featureFlagName) as T;
  const { cookieVariant, cookiePayload } = useFeatureFlagCookieData<T, VariantType>(featureFlagName);

  const resolveVariantData = () => {
    // If the feature flag is not enabled, return the cookie variant of the feature flag (which is fetched in the Middleware).
    if (!isFeatureEnabled) {
      return transformMultiVariantData<T>(
        typeof cookieVariant === 'string' && payloads && typeof payloads === 'object' && cookieVariant in payloads
          ? (cookieVariant as VariantType)
          : bootstrapVariant,
        payloads,
      );
    }

    const isNewVariant = checkIsNewVariant(
      isFeatureEnabled || false,
      postHogVariant as string,
      postHogPayload,
      payloads,
      bootstrapVariant,
      cookieVariant as VariantType,
      cookiePayload as T,
    );

    // If the feature flag is set in PostHog (i.e. PostHog is ready and the user has opted in), use the PostHog variant.
    if (postHogVariant)
      return transformMultiVariantData<T>(
        postHogVariant as VariantType,
        isNewVariant ? { [postHogVariant as VariantType]: postHogPayload } : payloads,
      );
    // If the feature flag is not set in PostHog, check the cookie if the feature flag is set there.
    if (cookieVariant && cookiePayload) return transformMultiVariantData<T>(cookieVariant, cookiePayload);
    // If the feature flag is not set in the cookie, use the bootstrapVariant which is the default variant of the feature flag.
    return transformMultiVariantData<T>(bootstrapVariant, payloads);
  };

  // The flag module is imported asynchronously, and until it lands `payloads` is undefined — which makes the branch
  // above ignore the middleware's cookie variant, so every user reads as `control`. Callers that branch on `isTest`
  // must wait for `isLoading` to clear, otherwise they act on a variant the user isn't actually in.
  return { ...resolveVariantData(), isLoading: !isSettled };
};
